import React, { useEffect, useState } from 'react';
import Swal from '../Librarian/librarianSwal';
import { useActiveLibraryId } from '../Constant/useActiveLibrary';
import Loader from '../Layout/Loader';
import useMinLoading from '../Layout/useMinLoading';
import {
  getAllLibrariesAdmin,
  updateLibraryAdmin,
  getErrorMessage
} from '../api/api';
import '../Librarian/lib-page-header.css';
import '../Librarian/BookList.css';

/**
 * "My School" — the School Admin can view and edit their own school's basic
 * details (name / code). They cannot create or delete schools (that's the Super
 * Admin's job), so this is a single-record editor scoped to their library_id.
 */
const SchoolProfile = () => {
  const libraryId = useActiveLibraryId();

  const [loading, setLoading] = useState(false);
  const showLoader = useMinLoading(loading);
  const [school, setSchool] = useState(null);
  const [form, setForm] = useState({ library_name: '', library_code: '' });
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!libraryId) return;
    try {
      setLoading(true);
      const libs = await getAllLibrariesAdmin();
      const mine = (Array.isArray(libs) ? libs : []).find((l) => String(l.id) === String(libraryId));
      setSchool(mine || null);
      setForm({
        library_name: mine?.library_name || '',
        library_code: mine?.library_code || ''
      });
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryId]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.library_name.trim() || !form.library_code.trim()) {
      Swal.fire('Missing fields', 'School name and code are required.', 'warning');
      return;
    }
    try {
      setSaving(true);
      await updateLibraryAdmin(school.id, {
        library_name: form.library_name,
        library_code: form.library_code,
        employee_authorization_list_id: school.employee_id || school.employee_authorization_list_id || ''
      });
      Swal.fire('Saved', 'School details updated.', 'success');
      setEditing(false);
      load();
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-fluid mt-3">
      <div className="lib-page-header ms-0">
        <div className="lib-page-header__left">
          <div className="lib-page-header__icon"><i className="fas fa-school" /></div>
          <div>
            <h3 className="lib-page-header__title">My School</h3>
            <div className="lib-page-header__subtitle">View and update your school's details</div>
          </div>
        </div>
        {school && !editing && (
          <div className="lib-page-header__actions">
            <button type="button" className="book-add-btn" onClick={() => setEditing(true)}>
              <i className="fas fa-pen" /> Edit
            </button>
          </div>
        )}
      </div>

      {showLoader ? (
        <Loader />
      ) : !libraryId ? (
        <div className="book-table-card"><div className="book-empty" style={{ padding: 40 }}>No school is linked to your account.</div></div>
      ) : !school ? (
        <div className="book-table-card"><div className="book-empty" style={{ padding: 40 }}>Your school could not be found.</div></div>
      ) : (
        <div className="book-table-card" style={{ padding: 24 }}>
          {editing ? (
            <div className="cat-form" style={{ maxWidth: 560 }}>
              <div className="cat-form-row">
                <div className="cat-form-group">
                  <label>School / Library Name <span>*</span></label>
                  <input value={form.library_name} onChange={(e) => setForm({ ...form, library_name: e.target.value })} />
                </div>
                <div className="cat-form-group">
                  <label>School Code <span>*</span></label>
                  <input value={form.library_code} onChange={(e) => setForm({ ...form, library_code: e.target.value })} />
                </div>
              </div>
              <div className="cat-form-actions">
                <button className="cat-btn-save" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
                <button className="cat-btn-cancel" onClick={() => { setEditing(false); load(); }}>Cancel</button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 20 }}>
              <Detail label="School Name" value={school.library_name} />
              <Detail label="School Code" value={school.library_code} />
              <Detail label="School ID" value={school.id} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Detail = ({ label, value }) => (
  <div>
    <div style={{ fontSize: 12, fontWeight: 700, color: '#8a96ad', textTransform: 'uppercase', letterSpacing: '.04em' }}>
      {label}
    </div>
    <div style={{ fontSize: 18, fontWeight: 700, color: '#1f2d50', marginTop: 4 }}>{value || '—'}</div>
  </div>
);

export default SchoolProfile;
